import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useBlackFriday, BLACK_FRIDAY_CODE } from '@/hooks/useBlackFriday';
import heroImg from '@/assets/home-v2/familia-1kg.webp.asset.json';
import { RatingLine, Eyebrow } from './shared';

const SEALS = ['Sem perfume', 'Livre de parabenos', 'Hipoalergênico', 'Notificado na ANVISA'];
const MARQUEE = [
  'Sem perfume',
  'Toque seco, não pesa no calor',
  'Testado em HRIPT',
  'Formulado para pele brasileira',
  'Sem parabenos',
  'Hipoalergênico',
];

export const HeroV2 = () => {
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Zoom suave (1 → 1.2) conforme a foto atravessa a tela.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const frame = frameRef.current;
      const img = imgRef.current;
      if (!frame || !img) return;
      const r = frame.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
      img.style.transform = `scale(${1 + progress * 0.2})`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const loop = [...MARQUEE, ...MARQUEE];
  const blackFriday = useBlackFriday();
  const copyCode = () => {
    navigator.clipboard?.writeText(BLACK_FRIDAY_CODE)
      .then(() => toast.success(`Cupom ${BLACK_FRIDAY_CODE} copiado. Cole no checkout.`, { position: 'top-center' }))
      .catch(() => toast.info(`Use o cupom ${BLACK_FRIDAY_CODE} no checkout.`, { position: 'top-center' }));
  };

  return (
    <section id="home-top" className="bg-v2-bg text-v2-ink overflow-hidden">
      <Helmet>
        <link rel="preload" as="image" href={heroImg.url} fetchPriority="high" />
      </Helmet>
      <div className="container mx-auto px-5 pt-12 md:pt-20 text-center max-w-4xl">
        <Eyebrow>Os especialistas em pele sensível</Eyebrow>
        <h1 className="font-narrow font-semibold tracking-tight text-[2.6rem] leading-[1.02] md:text-7xl">
          Enfim, o hidratante que <span className="text-v2-blue">sua pele pede.</span>
        </h1>
        <p className="mt-5 text-base text-v2-muted md:text-lg max-w-xl mx-auto">
          Sem perfume, testado em laboratório independente e feito no Brasil. Para quem já desistiu de creme que arde.
        </p>
        {blackFriday && (
          <div className="mt-5" data-testid="hero-black-friday">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-2xl sm:rounded-full border border-v2-blue/40 bg-v2-blue/10 px-4 py-2.5 text-sm text-v2-ink">
              <span className="font-bold tracking-wide text-v2-blue">BLACK FRIDAY</span>
              <span>15% em pedidos a partir de R$ 120</span>
              <button
                type="button"
                onClick={copyCode}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-v2-blue bg-v2-bg px-3 py-1.5 font-bold tracking-wider hover:bg-white transition-colors"
                aria-label={`Copiar cupom ${BLACK_FRIDAY_CODE}`}
              >
                {BLACK_FRIDAY_CODE}
                <Copy className="w-3.5 h-3.5 text-v2-blue" strokeWidth={2.5} />
              </button>
            </div>
            <p className="mt-2 text-xs text-v2-muted">
              De 20 a 30 de novembro · 1 uso por cliente · não acumula com outros cupons · frete grátis como sempre
            </p>
          </div>
        )}
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="#escolha" className="inline-flex items-center justify-center min-h-12 px-7 rounded-full bg-v2-ink text-v2-bg font-semibold hover:bg-v2-ink/90 transition-colors">
            {blackFriday ? 'Montar minha rotina' : 'Escolher o meu'}
          </a>
          <a href="#prova" className="inline-flex items-center justify-center min-h-12 px-7 rounded-full border border-v2-ink/25 font-semibold hover:bg-v2-ink/5 transition-colors">
            Por que confiar
          </a>
        </div>
        <RatingLine className="mt-5" />
      </div>

      <div className="container mx-auto px-4 mt-10 max-w-6xl">
        <div ref={frameRef} className="rounded-3xl overflow-hidden aspect-[4/3] md:aspect-[3/2] bg-v2-blue/10">
          <img
            ref={imgRef}
            src={heroImg.url}
            alt="Família reunida no sofá"
            width={1400}
            height={933}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center will-change-transform origin-center"
          />
        </div>
        <ul className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-v2-muted md:text-sm">
          {SEALS.map((s) => (
            <li key={s} className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-v2-blue" strokeWidth={2.5} /> {s}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-10 border-y border-v2-ink/10 py-4 overflow-hidden" aria-label="Diferenciais NAT HOF">
        <div className="flex w-max whitespace-nowrap" style={{ animation: 'announcement-marquee 80s linear infinite' }}>
          {loop.map((m, i) => (
            <span key={i} className="font-narrow text-lg md:text-2xl font-medium px-6 inline-flex items-center gap-6" aria-hidden={i >= MARQUEE.length}>
              {m}
              <span className="w-1.5 h-1.5 rounded-full bg-v2-blue" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
